import React, { useState, useMemo } from 'react';
import { TYPES, TYPE_CHART } from '../data/typeChart';
import { TYPE_COLORS } from './TeamBrowser';

function TypeChart() {
  const [selectedTypes, setSelectedTypes] = useState([]);

  const toggleType = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) return prev.filter(t => t !== type);
      if (prev.length >= 2) return [prev[1], type]; // max 2, replace oldest
      return [...prev, type];
    });
  };

  // Calculate combined defensive matchup for selected types
  const defensiveMatchup = useMemo(() => {
    if (selectedTypes.length === 0) return null;

    const result = { immune: [], quad_weak: [], weak: [], resist: [], quad_resist: [], neutral: [] };

    for (const atkType of TYPES) {
      let multiplier = 1;
      for (const defType of selectedTypes) {
        multiplier *= TYPE_CHART[atkType]?.[defType] ?? 1;
      }
      if (multiplier === 0) result.immune.push(atkType);
      else if (multiplier >= 4) result.quad_weak.push(atkType);
      else if (multiplier >= 2) result.weak.push(atkType);
      else if (multiplier <= 0.25) result.quad_resist.push(atkType);
      else if (multiplier < 1) result.resist.push(atkType);
      else result.neutral.push(atkType);
    }
    return result;
  }, [selectedTypes]);

  // Calculate offensive coverage for selected types
  const offensiveMatchup = useMemo(() => {
    if (selectedTypes.length === 0) return null;

    const result = { super: [], neutral: [], resist: [], immune: [] };

    for (const defType of TYPES) {
      // best effectiveness any of the selected types have against this defending type
      let best = 0;
      for (const atkType of selectedTypes) {
        const eff = TYPE_CHART[atkType]?.[defType] ?? 1;
        if (eff > best) best = eff;
      }
      if (best === 0) result.immune.push(defType);
      else if (best >= 2) result.super.push(defType);
      else if (best < 1) result.resist.push(defType);
      else result.neutral.push(defType);
    }
    return result;
  }, [selectedTypes]);

  return (
    <div className="type-chart-page">
      <h2>Interactive Type Calculator</h2>
      <p className="subtitle">Click 1 or 2 types to see what's strong and weak against that typing.</p>

      {/* Type selector */}
      <div className="type-picker">
        {TYPES.map(t => (
          <button
            key={t}
            className={`type-pick-btn ${selectedTypes.includes(t) ? 'picked' : ''}`}
            style={{
              backgroundColor: selectedTypes.includes(t) ? TYPE_COLORS[t] : 'transparent',
              borderColor: TYPE_COLORS[t],
              color: selectedTypes.includes(t) ? '#fff' : TYPE_COLORS[t],
            }}
            onClick={() => toggleType(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {selectedTypes.length > 0 && (
        <button className="clear-types-btn" onClick={() => setSelectedTypes([])}>
          Clear
        </button>
      )}

      {/* Results */}
      {defensiveMatchup && (
        <div className="matchup-results">
          <div className="matchup-header">
            <span className="matchup-label">Analyzing:</span>
            <div className="matchup-selected-types">
              {selectedTypes.map(t => (
                <span key={t} className="type-badge-lg" style={{ backgroundColor: TYPE_COLORS[t] }}>
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="matchup-columns">
            {/* Defensive */}
            <div className="matchup-col">
              <h3>Defending — what hits you</h3>

              {defensiveMatchup.quad_weak.length > 0 && (
                <div className="matchup-group">
                  <div className="matchup-group-label quad-weak-label">4x Weak</div>
                  <div className="matchup-group-types">
                    {defensiveMatchup.quad_weak.map(t => (
                      <span key={t} className="type-badge" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {defensiveMatchup.weak.length > 0 && (
                <div className="matchup-group">
                  <div className="matchup-group-label weak-label">2x Weak</div>
                  <div className="matchup-group-types">
                    {defensiveMatchup.weak.map(t => (
                      <span key={t} className="type-badge" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {defensiveMatchup.neutral.length > 0 && (
                <div className="matchup-group">
                  <div className="matchup-group-label neutral-label">1x Neutral</div>
                  <div className="matchup-group-types">
                    {defensiveMatchup.neutral.map(t => (
                      <span key={t} className="type-badge" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {defensiveMatchup.resist.length > 0 && (
                <div className="matchup-group">
                  <div className="matchup-group-label resist-label">1/2x Resist</div>
                  <div className="matchup-group-types">
                    {defensiveMatchup.resist.map(t => (
                      <span key={t} className="type-badge" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {defensiveMatchup.quad_resist.length > 0 && (
                <div className="matchup-group">
                  <div className="matchup-group-label quad-resist-label">1/4x Resist</div>
                  <div className="matchup-group-types">
                    {defensiveMatchup.quad_resist.map(t => (
                      <span key={t} className="type-badge" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {defensiveMatchup.immune.length > 0 && (
                <div className="matchup-group">
                  <div className="matchup-group-label immune-label">0x Immune</div>
                  <div className="matchup-group-types">
                    {defensiveMatchup.immune.map(t => (
                      <span key={t} className="type-badge" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Offensive */}
            <div className="matchup-col">
              <h3>Attacking — what you hit</h3>

              {offensiveMatchup.super.length > 0 && (
                <div className="matchup-group">
                  <div className="matchup-group-label super-label">Super Effective</div>
                  <div className="matchup-group-types">
                    {offensiveMatchup.super.map(t => (
                      <span key={t} className="type-badge" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {offensiveMatchup.neutral.length > 0 && (
                <div className="matchup-group">
                  <div className="matchup-group-label neutral-label">Neutral</div>
                  <div className="matchup-group-types">
                    {offensiveMatchup.neutral.map(t => (
                      <span key={t} className="type-badge" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {offensiveMatchup.resist.length > 0 && (
                <div className="matchup-group">
                  <div className="matchup-group-label resist-label">Not Very Effective</div>
                  <div className="matchup-group-types">
                    {offensiveMatchup.resist.map(t => (
                      <span key={t} className="type-badge" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {offensiveMatchup.immune.length > 0 && (
                <div className="matchup-group">
                  <div className="matchup-group-label immune-label">No Effect</div>
                  <div className="matchup-group-types">
                    {offensiveMatchup.immune.map(t => (
                      <span key={t} className="type-badge" style={{ backgroundColor: TYPE_COLORS[t] }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full reference table below */}
      <details className="full-chart-toggle">
        <summary>Full 18x18 Type Chart Reference</summary>
        <div className="type-chart-scroll">
          <table className="type-chart-table">
            <thead>
              <tr>
                <th className="corner-cell">ATK / DEF</th>
                {TYPES.map(t => (
                  <th key={t} className="type-header" style={{ backgroundColor: TYPE_COLORS[t] }}>
                    {t.slice(0, 3)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TYPES.map(atkType => (
                <tr key={atkType}>
                  <td className="type-row-header" style={{ backgroundColor: TYPE_COLORS[atkType] }}>
                    {atkType}
                  </td>
                  {TYPES.map(defType => {
                    const val = TYPE_CHART[atkType][defType];
                    let cls = 'cell-neutral';
                    let txt = '';
                    if (val === 0) { cls = 'cell-immune'; txt = '0'; }
                    else if (val === 0.5) { cls = 'cell-resist'; txt = '\u00BD'; }
                    else if (val === 2) { cls = 'cell-super'; txt = '2'; }
                    return (
                      <td key={defType} className={`type-cell ${cls}`} title={`${atkType} vs ${defType}: ${val}x`}>
                        {txt}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

export default TypeChart;
